import React, { memo, useEffect, useRef, useState } from "react";
import ReactTooltip from "react-tooltip";
import Loader from "../../../common/components/Loader";
import { WildCardFilterItem } from "./WildCardFilterItem";
import { areEqual } from "react-window";
import _ from "lodash";
import { EllipsisTooltipText } from "../../../common/components/EllipsisTextWithTooltip";
import StartIcon from "../../../../assets/img/star.svg";
import StartActiveIcon from "../../../../assets/img/star-active.svg";
import { sanitizeHtml } from "../../../common/components/SafeHtml";
import { getWildcardStorageKey, hasListDropdown, isWildcardOnlyField } from "../../filterFieldBuilders";

export const FilterItem = memo(
    ({
        elem,
        showDropDown,
        handleFilterView,
        isLoading,
        options = [],
        totalCount,
        handleFilterUpdate,
        filterData: filterParams,
        yearValues,
        setShowDropdown,
        setWildCardFilter,
        isAssetFilter,
        isSmartChartFilter = false,
        isCoreReportFilters = false,
        core_report_req_filters = {},
        isDashboard = false,
        favorites,
        toggleFavorite,
        isRecommendationTable = false,
        showWildcard,
        setShowWildcard,
        showWildcardField
    }) => {
        const isPriority = elem?.isPriority || false;
        const priorityTipId = useRef(`priority-filter-tip-${elem?.key || Math.random().toString(36).substr(2, 6)}`);
        const initialData =
            elem.key === "year"
                ? {
                      start_year: filterParams?.start_year || "",
                      end_year: filterParams?.end_year || "",
                      update_fci: filterParams?.update_fci ?? true
                  }
                : [...(filterParams?.[elem.paramKey] || []), ...(filterParams?.exclude_filters?.[elem.paramKey] || [])] || [];
        const [filterData, setFilterData] = useState(initialData);
        const [search, setSearch] = useState("");
        const [yearErrorMessage, setYearErrorMessage] = useState("");
        const dropdownRef = useRef();
        const [exclude, setExclude] = useState(false);

        useEffect(() => {
            setFilterData(initialData);
            if (filterParams?.exclude_filters?.[elem.paramKey]?.length > 0) {
                setExclude(true);
            } else {
                setExclude(false);
            }
        }, [filterParams, showDropDown]);
        const handleChange = (item, label) => {
            if (label === "FCA Projects" && (isCoreReportFilters || isSmartChartFilter)) {
                const tempList = [item];
                setFilterData(tempList);
            } else {
                const tempList = filterData.includes(item) ? filterData.filter(elem => elem !== item) : [...filterData, item];
                setFilterData(tempList);
            }
        };

        const handleChangeYear = (e, clear) => {
            if (clear) {
                setFilterData({
                    ...filterData,
                    start_year: "",
                    end_year: "",
                    // update_fci: ""
                });
                setYearErrorMessage("");
            } else {
                const { name, value, checked } = e.target;
                let endYear = {};
                if (name === "start_year" && parseInt(filterData?.end_year) < parseInt(value)) {
                    endYear = { end_year: parseInt(value) };
                }
                if (name === "start_year" && !value) {
                    endYear = { end_year: "" };
                }
                if (name === "start_year" && yearErrorMessage === "startYear" && value) {
                    setYearErrorMessage("");
                }
                if (name === "end_year" && yearErrorMessage === "endYear" && value) {
                    setYearErrorMessage("");
                }
                setFilterData({ ...filterData, [name]: name === "update_fci" ? checked : value ? parseInt(value) : "", ...endYear });
            }
        };

        const handleSelectAll = e => {
            setFilterData(e.target.checked ? searchList.map(opt => opt.id) : []);
        };

        const handleClearSelection = () => {
            setFilterData([]);
            setExclude(false);
        };

        if ((elem.key === "building_types" || isAssetFilter) && options.length) {
            options = options?.map(item => ({
                id: item.name,
                name: item.name === true ? "Yes" : item.name === false ? "No" : item.name,
                count: item.count
            }));
        }
        const isTruncated = typeof totalCount === "number" && totalCount > 0 && (options?.length || 0) < totalCount;
        const searchList =
            !search || isTruncated
                ? options
                : options?.filter(
                      item =>
                          item?.name?.toLowerCase()?.includes(search?.toLowerCase()) ||
                          item?.description?.toLowerCase()?.includes(search?.toLowerCase())
                  );
        const isDropdownOpen = showDropDown === elem.key;
        const [serverSearch, setServerSearch] = useState(null);
        useEffect(() => {
            if (!isDropdownOpen) {
                setServerSearch(null);
                return undefined;
            }
            if (!isTruncated) return undefined;
            const term = (search || "").trim();
            if (serverSearch === null ? !term : serverSearch === term) return undefined;
            const timer = setTimeout(() => setServerSearch(term), 500);
            return () => clearTimeout(timer);
        }, [search, isTruncated, isDropdownOpen, serverSearch]);
        useEffect(() => {
            if (serverSearch !== null) handleFilterView(elem, undefined, false, false, serverSearch);
        }, [serverSearch]);

        const availableOptions = searchList.filter(opt => elem.key !== "year" && !filterData.includes(opt.id));
        const selectedOptions = options.filter(opt => elem.key !== "year" && filterData.includes(opt.id));
        const isAllSelected = selectedOptions?.length > 0 && searchList.every(elem => elem.key !== "year" && filterData.includes(elem.id));
        const isFiltered =
            elem.key === "year"
                ? filterParams?.start_year || filterParams?.end_year
                : filterParams?.[elem.paramKey]?.length > 0 || filterParams?.exclude_filters?.[elem.paramKey]?.length > 0;
        useEffect(() => {
            if (selectedOptions.length || availableOptions.length) {
                ReactTooltip.rebuild();
            }
        }, [selectedOptions.length, availableOptions.length, options]);
        const isFavorite = favorites?.includes(elem.key);
        const wildcardFilterKey = getWildcardStorageKey(elem);
        const wildcardFilter = filterParams.filters?.[wildcardFilterKey] || {};

        if (isWildcardOnlyField(elem)) {
            return (
                <div key={elem.key} ref={dropdownRef} className={`col list-prop-sec dshb`}>
                    <WildCardFilterItem
                        elem={elem}
                        showDropDown={showWildcard}
                        setShowDropdown={setShowWildcard}
                        filter={wildcardFilter}
                        topNFilter={filterParams?.top_n?.[elem.paramKey] || {}}
                        setWildCardFilter={setWildCardFilter}
                        dropdownRef={dropdownRef}
                        handleFilterView={handleFilterView}
                        core_report_req_filters={core_report_req_filters}
                        isDashboard={isDashboard || false}
                        isRecommendationTable={isRecommendationTable}
                        isFavorite={isFavorite}
                        toggleFavorite={toggleFavorite}
                    />
                </div>
            );
        }

        return (
            <div key={elem.key} ref={dropdownRef} className={`col list-prop-sec dshb`}>
                {isPriority && (
                    <ReactTooltip
                        id={priorityTipId.current}
                        className="rc-tooltip-custom-class rich-text-tooltip"
                        effect="solid"
                        place="top"
                        backgroundColor="#007bff"
                        html={true}
                        getContent={dataTip => (dataTip ? sanitizeHtml(dataTip) : "")}
                    />
                )}
                <>
                    {hasListDropdown(elem) && (
                    <div className={showDropDown === elem.key ? "dropdown-container dropdown-open" : "dropdown-container"}>
                        {(isDashboard || isRecommendationTable) && (
                            <div className="d-flex pr-tot-outr">
                                <i
                                    data-tip={isFavorite ? "Remove from favorites" : "Add to favorites"}
                                    data-for="master_filter_dropdown"
                                    onClick={() => toggleFavorite(elem.key)}
                                >
                                    {isFavorite ? <img src={StartActiveIcon} alt="" /> : <img src={StartIcon} alt="" />}
                                </i>
                                <EllipsisTooltipText className="wild-card-filter-label" text={elem?.label} asLabel />
                            </div>
                        )}
                        {!isDashboard && !isRecommendationTable && <label>{""}</label>}
                        <div
                            className={`dropdown-toggle click-dropdown ${isFiltered ? "bg-th-filtered" : ""}`}
                            onClick={() => {
                                handleFilterView(elem, dropdownRef);
                                setYearErrorMessage("");
                            }}
                        >
                            <span className="text-drp-overflw">
                                {isDashboard || isRecommendationTable
                                    ? "Select"
                                    : elem.label + (isCoreReportFilters && core_report_req_filters[elem.paramKey]?.required ? "*" : "")}
                            </span>
                            <span className="close-reg">
                                <i className="fas fa-chevron-down"></i>
                            </span>
                        </div>

                        <div className={showDropDown === elem.key ? "dropdown-menu  dropdown-active " : "dropdown-menu "}>
                                {isLoading && serverSearch === null ? (
                                    <div className="col-md-12 p-4">
                                        <Loader />
                                    </div>
                                ) : options?.length || elem.key === "year" || serverSearch !== null ? (
                                    <>
                                        <div
                                            className={`dropdown-menu drop-filtr pos-abs ${elem.key !== "year" ? "dp-rcm-overflow" : ""} mt-0`}
                                            onClick={e => {
                                                e.stopPropagation();
                                            }}
                                        >
                                            <>
                                                {elem.key === "year" ? (
                                                    <YearFilter
                                                        year={filterData}
                                                        yearValues={(options ?? [])?.map(e => e?.id)}
                                                        handleChangeYear={handleChangeYear}
                                                        yearErrorMessage={yearErrorMessage}
                                                    />
                                                ) : (
                                                    <>
                                                        <div className="col-md-12 p-0 slt">
                                                            <span className="dropdown-item build_search_drp">
                                                                <input
                                                                    type="search"
                                                                    placeholder="Search..."
                                                                    value={search}
                                                                    onChange={e => setSearch(e.target.value)}
                                                                />
                                                                <i class="fas fa-times cursor-pointer cls-close" onClick={() => setSearch("")} />
                                                                <div className="filter-dd-head">
                                                                    <div className="filter-dd-head-row container-check fil-container">
                                                                    <label>
                                                                        {`Select All (${selectedOptions.length}/${
                                                                            selectedOptions?.length + availableOptions?.length
                                                                        })`}
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isAllSelected}
                                                                            onChange={handleSelectAll}
                                                                            disabled={
                                                                                (isCoreReportFilters || isSmartChartFilter) &&
                                                                                elem?.label === "FCA Projects"
                                                                            }
                                                                        />
                                                                        <span
                                                                            className={`checkmark ${
                                                                                (isCoreReportFilters || isSmartChartFilter) &&
                                                                                elem?.label === "FCA Projects"
                                                                                    ? "cursor-diabled"
                                                                                    : ""
                                                                            }`}
                                                                        ></span>
                                                                    </label>

                                                                    {(isDashboard || isRecommendationTable) && (
                                                                        <div className="exclude-container">
                                                                            <span className="dropdown-item">
                                                                                <label className="container-check">
                                                                                    Exclude
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={exclude}
                                                                                        onClick={() => {
                                                                                            setExclude(!exclude);
                                                                                        }}
                                                                                    />
                                                                                    <span className="checkmark"></span>
                                                                                </label>
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    <button
                                                                        className={`clear-btn-selection ${
                                                                            (isCoreReportFilters || isSmartChartFilter) &&
                                                                            elem?.label === "FCA Projects"
                                                                                ? "cursor-diabled"
                                                                                : ""
                                                                        }`}
                                                                        onClick={handleClearSelection}
                                                                        disabled={
                                                                            (isCoreReportFilters || isSmartChartFilter) &&
                                                                            elem?.label === "FCA Projects"
                                                                        }
                                                                    >
                                                                        Clear
                                                                    </button>
                                                                    </div>
                                                                    {isTruncated && (
                                                                        <div className="filter-dd-head-meta">{`Showing ${options?.length || 0} out of ${totalCount} results`}</div>
                                                                    )}
                                                                </div>
                                                            </span>
                                                        </div>
                                                        <>
                                                            {isLoading ? (
                                                                <div className="col-md-12 p-4">
                                                                    <Loader />
                                                                </div>
                                                            ) : elem.hasGroupBy ? (
                                                                <GroupBySelection
                                                                    elem={elem}
                                                                    options={searchList}
                                                                    handleChange={handleChange}
                                                                    filterData={filterData}
                                                                    setFilterData={setFilterData}
                                                                />
                                                            ) : (
                                                                <>
                                                                    {selectedOptions?.length > 0 && (
                                                                        <div className="col-md-12 p-0 slt-ara border-bottom">
                                                                            {selectedOptions.map(item => (
                                                                                <span className="dropdown-item notranslate" key={item.id}>
                                                                                    <label className="container-check d-flex align-items-center">
                                                                                        <span
                                                                                            className="text-short"
                                                                                            data-tip={
                                                                                                isPriority
                                                                                                    ? item.description || ""
                                                                                                    : item.description
                                                                                                      ? `${item.name} (${item.description})`
                                                                                                      : ""
                                                                                            }
                                                                                            data-for={isPriority ? priorityTipId.current : "master_filter_dropdown"}
                                                                                            data-tip-disable={!item.description}
                                                                                            onMouseEnter={e => {
                                                                                                if (isPriority && item.description) {
                                                                                                    ReactTooltip.show(e.currentTarget);
                                                                                                }
                                                                                            }}
                                                                                            onMouseLeave={() => {
                                                                                                if (isPriority) ReactTooltip.hide();
                                                                                            }}
                                                                                            title=""
                                                                                        >
                                                                                            {item.name || "-"}{" "}
                                                                                            {!isPriority && item.description ? `(${item.description})` : ""}
                                                                                        </span>
                                                                                        <span className="count-num"> ({item.count})</span>
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={true}
                                                                                            onChange={() => handleChange(item.id, elem?.label)}
                                                                                            disabled={
                                                                                                selectedOptions?.length === 1 &&
                                                                                                (isCoreReportFilters || isSmartChartFilter) &&
                                                                                                elem?.label === "FCA Projects"
                                                                                            }
                                                                                        />
                                                                                        <span
                                                                                            className={`checkmark ${
                                                                                                selectedOptions?.length === 1 &&
                                                                                                (isCoreReportFilters || isSmartChartFilter) &&
                                                                                                elem?.label === "FCA Projects"
                                                                                                    ? `cursor-disabled`
                                                                                                    : ""
                                                                                            }`}
                                                                                        ></span>
                                                                                    </label>
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    {availableOptions?.length > 0 && (
                                                                        <div className="col-md-12 p-0 slt-ara">
                                                                            {availableOptions.map(item => (
                                                                                <span className="dropdown-item notranslate" key={item.id}>
                                                                                    <label className="container-check d-flex align-items-center">
                                                                                        <span
                                                                                            className="text-short"
                                                                                            data-tip={
                                                                                                isPriority
                                                                                                    ? item.description || ""
                                                                                                    : item.description
                                                                                                      ? `${item.name} (${item.description})`
                                                                                                      : ""
                                                                                            }
                                                                                            data-for={isPriority ? priorityTipId.current : "master_filter_dropdown"}
                                                                                            data-tip-disable={!item.description}
                                                                                            onMouseEnter={e => {
                                                                                                if (isPriority && item.description) {
                                                                                                    ReactTooltip.show(e.currentTarget);
                                                                                                }
                                                                                            }}
                                                                                            onMouseLeave={() => {
                                                                                                if (isPriority) ReactTooltip.hide();
                                                                                            }}
                                                                                            title=""
                                                                                        >
                                                                                            {item.name || "-"}{" "}
                                                                                            {!isPriority && item.description ? `(${item.description})` : ""}
                                                                                        </span>
                                                                                        <span className="count-num"> ({item.count})</span>
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={false}
                                                                                            onChange={() => handleChange(item.id, elem?.label)}
                                                                                        />
                                                                                        <span className="checkmark"></span>
                                                                                    </label>
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    {!selectedOptions?.length && !availableOptions?.length && (
                                                                        <div className="col-md-12 p-2">No Data Found</div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </>
                                                    </>
                                                )}
                                            </>
                                            {/* <div className="col-md-6 no-wrap">NO MORE VALUES TO DISPLAY</div> */}
                                            <div className="col-md-12 mt-3 drp-btn">
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btnRgion mr-2"
                                                    onClick={() =>
                                                        elem?.label === "Years"
                                                            ? !filterData?.start_year && filterData?.end_year
                                                                ? setYearErrorMessage("startYear")
                                                                : !filterData?.end_year && filterData?.start_year
                                                                ? setYearErrorMessage("endYear")
                                                                : handleFilterUpdate(elem, filterData, exclude)
                                                            : handleFilterUpdate(elem, filterData, exclude)
                                                    }
                                                >
                                                    OK
                                                </button>
                                                <button type="button" className="btn btn-primary btnClr" onClick={() => handleFilterView(elem)}>
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="col-md-12 p-2"> No Data Found</div>
                                )}
                            </div>
                        </div>
                    )}
                    {showWildcardField && hasListDropdown(elem) && (
                        <WildCardFilterItem
                            elem={elem}
                            showDropDown={showWildcard}
                            setShowDropdown={setShowWildcard}
                            filter={wildcardFilter}
                            topNFilter={filterParams?.top_n?.[elem.paramKey] || {}}
                            setWildCardFilter={setWildCardFilter}
                            dropdownRef={dropdownRef}
                            handleFilterView={handleFilterView}
                            core_report_req_filters={core_report_req_filters}
                            isDashboard={isDashboard || false}
                            isRecommendationTable={isRecommendationTable}
                            compact
                        />
                    )}
                </>
            </div>
        );
    },
    areEqual
);
const YearFilter = ({ yearValues = [], year, updateFCI, handleChangeYear, yearErrorMessage }) => {
    const startYear = year?.start_year ?? null;
    const endYear = year?.end_year ?? null;
    return (
        <div className="drop-filtr">
            <div className="col-md-12 p-0 slt-ara">
                <div className="year-outer ">
                    <div
                        class="clear-list-sec col-md-12 text-right p-0"
                        onClick={e => {
                            handleChangeYear(e, "clear");
                        }}
                    >
                        <span class="edit-icn-bx">Clear</span>
                    </div>
                    <div className="col-xl-12 selecbox-otr p-0">
                        <span className="dropdown-item p-0">
                            <label>Start Year</label>
                            <div className={`custom-selecbox w-100 ${yearErrorMessage === "startYear" ? "error-border" : ""}`}>
                                <select
                                    value={year?.start_year}
                                    name={"start_year"}
                                    onChange={e => {
                                        handleChangeYear(e);
                                    }}
                                >
                                    <option value="">Select</option>
                                    {yearValues.map(yearOption => (
                                        <>
                                            {endYear && yearOption > endYear ? null : (
                                                <option key={yearOption} disabled={endYear && yearOption > endYear} value={yearOption}>
                                                    {yearOption}
                                                </option>
                                            )}
                                        </>
                                    ))}
                                </select>
                            </div>
                        </span>
                    </div>
                    <div className="col-xl-12 p-0 selecbox-otr">
                        <span className="dropdown-item  p-0">
                            <label>End Year</label>
                            <div className={`custom-selecbox w-100 ${yearErrorMessage === "endYear" ? "error-border" : ""}`}>
                                <select
                                    value={year?.end_year}
                                    name={"end_year"}
                                    onChange={e => {
                                        handleChangeYear(e);
                                    }}
                                >
                                    <option value="">Select</option>
                                    {yearValues.map(yearOption => (
                                        <>
                                            {startYear && yearOption < startYear ? null : (
                                                <option key={yearOption} value={yearOption}>
                                                    {yearOption}
                                                </option>
                                            )}
                                        </>
                                    ))}
                                </select>
                            </div>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const GroupBySelection = ({ elem, options, handleChange, filterData, setFilterData }) => {
    if (!options.length) return null;
    const groupedOptions = Object.entries(
        options.reduce((groups, item) => {
            const groupName = item.group || "Others";
            if (!groups[groupName]) {
                groups[groupName] = { items: [], totalCount: 0 };
            }
            groups[groupName].items.push(item);
            groups[groupName].totalCount += item.count || 0;
            return groups;
        }, {})
    )?.sort(([groupNameA], [groupNameB]) => {
        return (groupNameA === "Others") - (groupNameB === "Others") || groupNameA?.localeCompare(groupNameB);
    });

    const toggleGroup = (groupItems, isSelected) => {
        setFilterData(prevFilterData => {
            if (isSelected) {
                const newItems = groupItems.filter(item => !prevFilterData.includes(item));
                return [...prevFilterData, ...newItems];
            } else {
                return prevFilterData.filter(item => !groupItems.includes(item));
            }
        });
    };

    return (
        <div className="col-md-12 p-0 slt-ara">
            {groupedOptions.map(([groupName, groupData], idx) => {
                const { items: groupItems, totalCount } = groupData;
                const isGroupSelected = groupItems.every(item => filterData.includes(item.id));
                return (
                    <>
                        {idx > 0 ? <hr /> : null}
                        <div key={groupName} className="group-section">
                            <div className="container-check group-title d-flex align-items-center mb-2">
                                <label class="">
                                    <input
                                        type="checkbox"
                                        checked={isGroupSelected}
                                        onChange={() =>
                                            toggleGroup(
                                                groupItems.map(item => item.id),
                                                !isGroupSelected
                                            )
                                        }
                                    />
                                    <span class="checkmark"></span>
                                </label>
                                <span class="ml-1 text-short top-head">{groupName}</span>
                                <span className="count-num"> ({totalCount})</span>
                            </div>
                            {groupItems.map(item => (
                                <span className="dropdown-item notranslate" key={item.id}>
                                    <label className="container-check d-flex align-items-center">
                                        <span
                                            className="text-short"
                                            data-tip={`${item.description ? `${item.name} (${item.description})` : ""}`}
                                            data-for="master_filter_dropdown"
                                        >
                                            {item.name || "-"} {item.description ? `(${item.description})` : ""}
                                        </span>
                                        <span className="count-num"> ({item.count})</span>
                                        <input
                                            type="checkbox"
                                            checked={filterData.includes(item.id)}
                                            onChange={() => handleChange(item.id, elem?.label)}
                                        />
                                        <span className="checkmark"></span>
                                    </label>
                                </span>
                            ))}
                        </div>
                    </>
                );
            })}
        </div>
    );
};
